import React, { useEffect, useState } from "react";
import { Icon, LinkButton, Typography } from "@bca/ui-library";

import {
  AUCTION_MANAGEMENT_ROOT_URL,
  SEARCH_URL,
  VENDOR_PORTAL_URL,
} from "../../helpers/pageUrls";
import pushToDigitalData from "../../analytics/pushToDigitalData";
import { useRouter } from "next/router";
import { ParsedUrlQuery } from "querystring";
import { shallowEqual, useSelector } from "react-redux";
import { StoreType } from "../../redux/StoreType";
import { GET_USER_STATE } from "../../redux/slices/userSlice";
import { GET_VENDOR_IMPERSONATION_STATE } from "../../redux/slices/impersonationSlice";

enum PagesToReturnTo {
  SOLD = "sold",
  PROVISIONALS = "provisionals",
  VENDOR = "vendor",
  AUCTION_MANAGEMENT_VEHICLES = "vehicles",
}

const BackToResultsButton: React.FC<{
  vrm: any;
}> = (props) => {
  const router = useRouter();
  const { vrm } = props;

  const [filtersUrlQueryString, setFiltersUrlQueryString] =
    useState<string>("");
  const [stripedVrm, setStripedVrm] = useState<string>("");

  const [isVendor, setIsVendor] = useState<boolean>(false);
  const [isTrackedPage, setIsTrackedPage] = useState<boolean>(false);
  const [isProvisionals, setIsProvisionals] = useState<boolean>(false);
  const [isSold, setIsSold] = useState<boolean>(false);
  const [isAuctionManagementVehicles, setIsAuctionManagementVehicles] =
    useState<boolean>(false);

  const linkTextBackToSearch = filtersUrlQueryString
    ? "Back to Results"
    : "Back to Search";
  const linkTextBackToVendorPortal = "Back to Results";
  const linkTextBackToTrackedPage = "Back to Tracked";
  const linkTextBackToAuctionManagement = "Back to Vehicles";
  const linkText = isVendor
    ? linkTextBackToVendorPortal
    : isTrackedPage
    ? linkTextBackToTrackedPage
    : isAuctionManagementVehicles
    ? linkTextBackToAuctionManagement
    : linkTextBackToSearch;

  const getBackToPortalUrl = (domainUrl: string, page?: string) =>
    `${domainUrl}${page ? `/${page}` : ""}${
      filtersUrlQueryString === "?" ? "?" : `${filtersUrlQueryString}&`
    }vrm=${stripedVrm}`;

  const backToSearch = filtersUrlQueryString
    ? `${SEARCH_URL}${filtersUrlQueryString}&vrm=${stripedVrm}`
    : `${SEARCH_URL}?vrm=${stripedVrm}`;

  function removeWhiteSpace(vrm: string) {
    return vrm.replace(/\s+/g, "");
  }

  useEffect(() => {
    setStripedVrm(removeWhiteSpace(vrm.vrm));
  }, [vrm]);

  const getFiltersUrlQueryString = (
    originalQuery: ParsedUrlQuery,
    sourcePage: PagesToReturnTo
  ): string => {
    const query = Object.assign({}, originalQuery);
    let path = "";
    delete query.vdr;
    delete query.requestOrigin;

    switch (sourcePage) {
      case PagesToReturnTo.SOLD:
        delete query.source;
        delete query.inventoryID;
        delete query.filterDate;
        break;
      case PagesToReturnTo.PROVISIONALS:
        delete query.source;
        break;
      case PagesToReturnTo.AUCTION_MANAGEMENT_VEHICLES:
        delete query.source;
        break;
    }

    let counter = 0;
    for (const property in query) {
      counter++;
      if (counter === Object.keys(query).length)
        path += `${property}=${query[property]}`;
      else path += `${property}=${query[property]}&`;
    }
    if (path.includes("|IsProvisional:false"))
      return path.replace("|IsProvisional:false", "");
    return path.replace("IsProvisional:false", "");
  };

  useEffect(() => {
    if (isSold) {
      setFiltersUrlQueryString(
        `?${getFiltersUrlQueryString(router.query, PagesToReturnTo.SOLD)}`
      );
    } else if (isProvisionals) {
      setFiltersUrlQueryString(
        `?${getFiltersUrlQueryString(
          router.query,
          PagesToReturnTo.PROVISIONALS
        )}`
      );
    } else if (isVendor) {
      setFiltersUrlQueryString(
        `?${getFiltersUrlQueryString(router.query, PagesToReturnTo.VENDOR)}`
      );
    } else if (isAuctionManagementVehicles) {
      setFiltersUrlQueryString(
        `?${getFiltersUrlQueryString(
          router.query,
          PagesToReturnTo.AUCTION_MANAGEMENT_VEHICLES
        )}`
      );
    } else {
      let searchParams = new URL(window.location.href).searchParams;
      searchParams.delete("searchVersion");
      setFiltersUrlQueryString(`?${searchParams.toString()}`);
    }
  }, [
    filtersUrlQueryString,
    isVendor,
    isProvisionals,
    isSold,
    isAuctionManagementVehicles,
  ]);

  useEffect(() => {
    setIsVendor(window.location.search.includes("vdr=vp"));
    setIsProvisionals(window.location.search.includes("source=Provisionals"));
    setIsSold(window.location.search.includes("source=Sold"));
    setIsTrackedPage(window.location.search.includes("requestOrigin=Tracked"));
    setIsAuctionManagementVehicles(
      window.location.search.includes("source=AuctionManagementVehicles")
    );
  }, []);

  const {
    user: { isVendorAdmin },
  }: StoreType = useSelector(GET_USER_STATE, shallowEqual);
  const {
    impersonation: { isVendorImpersonationSet },
  }: StoreType = useSelector(GET_VENDOR_IMPERSONATION_STATE, shallowEqual);

  const pushBackToSearchAnalytics = () => {
    if (
      (isVendorAdmin && isVendorImpersonationSet) ||
      isVendorAdmin === false
    ) {
      pushToDigitalData({
        event: {
          name: "click",
          category: "button",
          label: "back-to-results",
          action: "navigate",
        },
      });
    }
  };

  return (
    <LinkButton
      data-testid="return-link"
      variant="tertiary"
      px={0}
      width="auto"
      href={
        isProvisionals
          ? getBackToPortalUrl(VENDOR_PORTAL_URL, PagesToReturnTo.PROVISIONALS)
          : isSold
          ? getBackToPortalUrl(VENDOR_PORTAL_URL, PagesToReturnTo.SOLD)
          : isVendor
          ? getBackToPortalUrl(VENDOR_PORTAL_URL)
          : isAuctionManagementVehicles
          ? getBackToPortalUrl(
              AUCTION_MANAGEMENT_ROOT_URL,
              PagesToReturnTo.AUCTION_MANAGEMENT_VEHICLES
            )
          : backToSearch
      }
      onClick={isVendor ? null : pushBackToSearchAnalytics}
      shouldOpenInNewWindow={false}
      {...props}
    >
      <Icon
        width="16px"
        name="arrow-left"
        weight="regular"
        color="primary-white"
        mr={2}
        mb="2px"
        css={`
          flex-shrink: 0;
        `}
      ></Icon>
      <Typography
        color="primary-white"
        variant="body"
        my="0"
        fontSize={14}
        css={`
          text-decoration: underline;
        `}
      >
        {linkText}
      </Typography>
    </LinkButton>
  );
};

export default BackToResultsButton;
